import { makeAutoObservable, reaction, runInAction } from 'mobx'
import type { QueryClient } from '@tanstack/react-query'
import { Exercise, ExerciseProxy } from '@entities/exercise'
import { generateFilters } from '@shared/helpers/generateSearchParams'
import { Filter } from '@shared/types/searchParams'
import { PaginationResult } from '@shared/types/pagination'
import { ExerciseFilterStore } from './filters.store'
import { debounce } from '@shared/lib/debounce'

const FILTER_REFRESH_DEBOUNCE_MS = 220
const EXERCISES_QUERY_STALE_TIME = Infinity
const EXERCISES_QUERY_CACHE_TIME = 1000 * 60 * 30

type ExerciseRequestContext = {
	filter: Filter[]
	limit: number
	searchTerm: string
	start: number
}

const exerciseQueryKeys = {
	page: (context: ExerciseRequestContext) => [
		'training-constructor',
		'exercises',
		context,
	],
}

export class ExerciseStore {
	private exercises_: Exercise[] = []
	private filterStore: ExerciseFilterStore
	private queryClient?: QueryClient
	private readonly paginationLimit: number = 20
	private areMoreRequestsAvailable: boolean = true
	private isLoading: boolean = false
	private isFetching: boolean = false
	private activeRequestId: number = 0
	private shouldFetchAfterCurrentRequest: boolean = false
	private readonly debouncedRefreshExercises = debounce(() => {
		this.fetchExercises()
	}, FILTER_REFRESH_DEBOUNCE_MS)

	constructor(filterStore: ExerciseFilterStore) {
		makeAutoObservable(this, {
			queryClient: false,
		})
		this.filterStore = filterStore
		reaction(
			() => this.filterStore.getFtsSearchTerm(),
			() => {
				this.refreshExercises()
			},
		)
		reaction(
			() => this.filterStore.acceptedFilters,
			() => {
				this.refreshExercises()
			},
		)
	}

	public get exercises(): Exercise[] {
		return this.exercises_
	}

	public get isLoadingExercises(): boolean {
		return this.isLoading
	}

	public resetExercises = () => {
		this.exercises_ = []
		this.areMoreRequestsAvailable = true
	}

	public getExercises = (queryClient?: QueryClient) => {
		if (!this.areMoreRequestsAvailable) {
			return
		}

		this.queryClient = queryClient ?? this.queryClient

		if (this.queryClient && this.applyCachedExercises(this.queryClient)) {
			return
		}

		this.isLoading = true
		this.fetchExercises()
	}

	private refreshExercises = () => {
		this.activeRequestId += 1
		this.shouldFetchAfterCurrentRequest = false
		this.resetExercises()
		this.isLoading = true
		this.debouncedRefreshExercises()
	}

	private buildFiltersArray = (): Filter[] => {
		const filters = this.filterStore.acceptedFilters

		let aiFilter: Filter | null = null
		let exerciseTypeFilter: Filter | null = null
		let extraEquipmentsFilter: Filter | null = null
		let includedMusclesFilter: Filter | null = null

		if (filters.isAiAvailable.isSet && filters.isAiAvailable.value) {
			aiFilter = generateFilters({
				property: 'is_ai_available',
				value: filters.isAiAvailable.value,
				operator: 'eq',
				independent: false,
				disableOnEmpty: false,
			})
		}

		if (filters.exerciseType.isSet && filters.exerciseType.value) {
			exerciseTypeFilter = generateFilters({
				value: filters.exerciseType.value,
				property: 'type',
				disableOnEmpty: false,
				independent: false,
				operator: 'eq',
			})
		}

		if (filters.extraEquipments.isSet && filters.extraEquipments.value) {
			extraEquipmentsFilter = generateFilters({
				property: 'equipment_tags',
				value: [filters.extraEquipments.value],
				disableOnEmpty: false,
				independent: false,
				operator: 'any',
			})
		}

		if (filters.includedMuscleCodes.length !== 0) {
			includedMusclesFilter = generateFilters({
				value: filters.includedMuscleCodes,
				property: 'muscle_group_codes',
				operator: 'any',
				disableOnEmpty: false,
				independent: false,
			})
		}

		return [
			aiFilter,
			exerciseTypeFilter,
			extraEquipmentsFilter,
			includedMusclesFilter,
		].filter(
			(filter): filter is Filter => filter !== null && filter !== undefined,
		)
	}

	private buildRequestContext = (): ExerciseRequestContext => ({
		filter: this.buildFiltersArray(),
		limit: this.paginationLimit,
		searchTerm: this.filterStore.getFtsSearchTerm(),
		start: this.exercises_.length,
	})

	private fetchExercisesPage = async (
		context: ExerciseRequestContext,
	): Promise<PaginationResult<Exercise>> => {
		const params = {
			limit: context.limit,
			start: context.start,
			sort: [],
			independent_fields: null,
			page: 0,
			filter: context.filter,
		}

		return context.searchTerm !== ''
			? ExerciseProxy.FtsSearch(params, context.searchTerm)
			: ExerciseProxy.search(params)
	}

	private appendExercisesPage = (result: PaginationResult<Exercise>) => {
		const newExercises = result.rows.filter(
			(newExercise) =>
				!this.exercises_.some(
					(existing) => existing.id_exercise === newExercise.id_exercise,
				),
		)

		this.exercises_ = [...this.exercises_, ...newExercises]
		this.areMoreRequestsAvailable = result.rows.length === this.paginationLimit
		this.isLoading = false
	}

	private applyCachedExercises = (queryClient: QueryClient): boolean => {
		const context = this.buildRequestContext()
		const cachedResult = queryClient.getQueryData<PaginationResult<Exercise>>(
			exerciseQueryKeys.page(context),
		)

		if (!cachedResult) {
			return false
		}

		this.appendExercisesPage(cachedResult)
		return true
	}

	private fetchExercises = async () => {
		if (!this.areMoreRequestsAvailable) {
			this.isLoading = false
			return
		}

		if (this.isFetching) {
			this.shouldFetchAfterCurrentRequest = true
			return
		}

		this.isFetching = true

		const requestId = this.activeRequestId
		const context = this.buildRequestContext()
		const queryClient = this.queryClient

		try {
			const result: PaginationResult<Exercise> =
				queryClient
					? await queryClient.fetchQuery({
						queryKey: exerciseQueryKeys.page(context),
						queryFn: () => this.fetchExercisesPage(context),
						staleTime: EXERCISES_QUERY_STALE_TIME,
						cacheTime: EXERCISES_QUERY_CACHE_TIME,
					})
					: await this.fetchExercisesPage(context)

			if (requestId !== this.activeRequestId) {
				return
			}

			runInAction(() => {
				if (requestId !== this.activeRequestId) {
					return
				}

				this.appendExercisesPage(result)
			})
		} catch (e: unknown) {
			if (requestId === this.activeRequestId) {
				console.error('Error loading exercises:', e)
			}
		} finally {
			let shouldFetchNextPage = false

			runInAction(() => {
				this.isFetching = false

				if (this.shouldFetchAfterCurrentRequest) {
					this.shouldFetchAfterCurrentRequest = false
					shouldFetchNextPage = true
				} else if (requestId === this.activeRequestId) {
					this.isLoading = false
				}
			})

			if (shouldFetchNextPage) {
				this.fetchExercises()
			}
		}
	}
}
